import * as Sentry from '@sentry/react';
import { cookiesPermitted, setPreferWebXRCamera } from '@zappar/zappar-react-three-fiber';
import { createRoot } from 'react-dom/client';
import App from './components/App/App';
import { IComponentsById, IComponentType, IComponentUnion, IContentDoc, IRoot, ISceneComp, IScreenAnchorPositionType, ITrackingTypes } from '@r3f-component-data-structure';
import { IOverflowEdge } from './utils/screen-relative-overflow';
import { isValidContentDoc } from '@r3f-component-data-structure/validate/validate';
import { sceneSignature, sceneSignatureFor } from '@r3f-component-data-structure/utils';
import { evictModelsNotIn } from '@r3f/components/Model3d/hooks';
import { evictTexturesNotIn } from '@r3f/utils/loadAsset';
import { applyContentToStores } from './content-apply';
import { collectAssetUrls } from './asset-urls';
import { useAssetPreloader as preloadAssets } from './hooks/useAssetPreloader';
import { IAnalyticsProvider, IAnalyticsEvent, IAnalyticsTrackingDict } from '@r3f-component-data-structure/types/analytics';
import HintGraphicSvgUrl from '@r3f/components/assets/MotionPrompt.svg';
import { showDefaultErrorPage } from './default-pages/error-page/error-page';
import { GoogleAnalyticsProvider } from './utils/analytics/googleAnalyticsProvider';
import { MsClarityAnalyticsProvider } from './utils/analytics/msClarityAnalyticsProvider';
import { debugFormat, debugLog, setDebugLogging } from './debug-log';
import { IEntityActionTypes, IMode, IUserActions, useActionStateStore, useDataStore, useUserStore } from './zustand';
import { LmsClient, getLmsClient } from './lms-client';
import { isSafariWithAudioContextBug } from '@r3f/utils/general';
import { intialiseWebxrSession } from './components/TrackerGroups/WorldXRTracker/webxr-utils';
import { TEST_SOURCE_FILE_NAME } from '../playwright/visual-regression/utils/constants';
import { StaticGeometryWorkerController } from '@r3f/staticGeometryWorkerController/StaticGeometryWorkerController';
import { BVHWorkerController } from '@r3f/BVHworkerController/BVHworkerController';
import { filestore } from './filestore';
import { isAppClip } from '@zappar/zappar';
import { Jute } from '.';
import { getTranslation, ILocale, ITranslationKey, translationMap } from './translations';

// import { GenerateMeshBVHWorker } from "three-mesh-bvh/src/workers/GenerateMeshBVHWorker.js";

/**
 * How long after a live content update to reclaim assets the new doc no longer references.
 *
 * Must exceed the longest scene transition: a scene mid-exit is still rendering assets that the new doc
 * has already dropped, and disposing those shows as a blank/black mesh with no error. Err long — the cost
 * of waiting is a few seconds of retained memory; the cost of being early is a corrupted frame.
 */
const EVICT_GRACE_MS = 10_000;

////
// ALLOW STANDALONE BUILDS TO GET THE NECESSARY EXTERNAL FILES
/* eslint-disable no-var */
declare var Z_STANDALONE: string | undefined;
declare var __webpack_public_path__: string | undefined;
/* eslint-enable no-var */

if (typeof Z_STANDALONE !== 'undefined' && Z_STANDALONE) {
	if (document.currentScript) {
		const url = new URL((document.currentScript as HTMLScriptElement).src);
		let href = url.toString();
		if (url.pathname) {
			let pathParts = href.split('/');
			pathParts.pop();
			href = pathParts.join('/') + '/';
		}
		/* eslint-disable @typescript-eslint/no-unused-vars */
		__webpack_public_path__ = href;
		/* eslint-enable @typescript-eslint/no-unused-vars */
	}
}
///////
declare const JUTE_VERSION: string; // Set by webpack
const ROOT_ID = 'jute_root';

type IOnFirstCameraFrameCb = () => unknown;

export type IEnvironment = 'production' | 'development' | 'test';

/** A frame source supplied by the embedding page in place of the device camera. */
export type ICameraSource = HTMLCanvasElement | HTMLVideoElement | HTMLImageElement;

/** The preview simulator's orbit camera, as the embedding page can drive it. */
export interface IPreviewCameraControls {
	orbit: (deltaAzimuth: number, deltaPolar: number) => void;
	/** Fractions of the visible height at the orbit target, positive right and up. */
	pan: (deltaRight: number, deltaUp: number) => void;
	/** The same unit, along the ground in the direction the camera faces. Positive walks forward. */
	walk: (deltaForward: number) => void;
	zoom: (factor: number) => void;
	reset: () => void;
}

/**
 * A 360 scene's camera, as the embedding page can turn it.
 *
 * Separate from `IPreviewCameraControls` because the two cameras have nothing in common to drive: an
 * orbit camera swings around a target, while this one stands inside the sphere and only ever turns on
 * the spot. There is no zoom — JUTE turns it off for 360 in both controls that could offer one.
 */
export interface IPreviewLookControls {
	look: (deltaAzimuth: number, deltaPolar: number) => void;
}

/** What `onScreenContentOverflow` hands a host. An empty `entityIds` means all clear. */
export interface IScreenContentOverflow {
	/** The scene measured. A report for a scene the host has moved on from is stale. */
	sceneId: string;
	entityIds: string[];
	/**
	 * The component type of each id, in the same order, so a host can say "a button" rather than "some
	 * UI". Sent because only the runtime has the doc: a host reading this from another document — the
	 * preview shell does — has no content doc of its own to look them up in.
	 */
	entityTypes: IComponentType[];
	/** The anchor groups those entities sit in, so a host can name the anchors that need changing. */
	anchors: IScreenAnchorPositionType[];
	edges: IOverflowEdge[];
}

interface IJuteInitInput {
	rootEl?: HTMLElement;
	launchButton?: HTMLElement;
	onLoaded?: () => unknown;
	onLoading?: () => unknown;
	onUnsupported?: () => unknown;
	onFirstCameraFrame?: () => unknown;
	/** The host will call `setCameraSource` before launch — don't fall back to the device camera. */
	cameraSourcePending?: boolean;
	/** Review content at a desk: orbit a plain scene at the origin, never open a camera. */
	previewSimulator?: boolean;
	/** Designer's live preview — the desktop simulator or the same project on a phone. Never a published experience. */
	preview?: boolean;
}
type IJuteInit = (args: IJuteInitInput) => void;
export class JuteManager {
	private _launchCbs: (() => any)[] = [];
	private _onSessionStartedCbs: ((session: XRSession) => any)[] = [];
	private _rootEl?: HTMLElement | HTMLDivElement | HTMLBodyElement;
	private _loadingStartDate?: Date;
	private _onReadyCbs: (() => any)[] = [];
	private _onTestEntityClickCbs: ((id: string) => any)[] = [];
	private _contentLoadedCbs: (() => any)[] = [];
	private _onLoading?: () => any;
	private _onUnsupported?: () => any;
	private _onTestTimeChange?: (t: number) => any;
	private _firstFrameVisible = false;
	private _onloadedBeforeFirstFrame = false;
	private _testTransitionTimeStepsMs?: number;
	private _environment: IEnvironment = 'production';
	private _version = JUTE_VERSION;
	private _versionSpan?: HTMLSpanElement;
	private _languageSelection?: HTMLSelectElement;
	private _analyticsProviderDict = {} as { -readonly [k in keyof typeof IAnalyticsProvider]: null | GoogleAnalyticsProvider | MsClarityAnalyticsProvider };
	private _onFirstCameraFrameCbs: IOnFirstCameraFrameCb[] = [];
	private _grantedPermissions = { camera: false, orientation: false, motion: false };
	private _sceneTypes: ITrackingTypes[] = []
	private _cameraSource?: ICameraSource;
	private _cameraSourceCbs: (() => unknown)[] = [];
	private _previewCameraControls?: IPreviewCameraControls;
	private _previewLookControls?: IPreviewLookControls;
	private _activeSceneCbs: ((sceneId: string, trackingType: ITrackingTypes) => unknown)[] = [];
	private _activeScene?: { sceneId: string; trackingType: ITrackingTypes };
	private _screenOverflowCbs: ((report: IScreenContentOverflow) => unknown)[] = [];
	private _screenOverflow?: IScreenContentOverflow;

	public isHeadset = this.isQuest || this.isZapbox || this.isPico;
	private _requiredPermissions = { camera: !this.isHeadset, orientation: false, motion: false };
	/** Set by the host via `cameraSourcePending`; true until `setCameraSource` actually delivers one. */
	public isCameraSourcePending = false;
	/**
	 * Set by the host via `previewSimulator`: this is Designer's desktop preview, being fed a synthetic
	 * camera source rather than a real one.
	 *
	 * It does NOT put the experience in non-AR mode. The preview stays in AR for the whole session so
	 * image and face scenes keep real tracking, and world-tracked scenes — which have nothing to track
	 * at a desk — are orbited instead by handing the render camera over per scene. See
	 * PREVIEW_OVERVIEW.md §10.
	 */
	public isPreviewSimulator = false;
	/**
	 * Set by the host via `preview`: Designer is driving this session, either through the desktop
	 * simulator or the cross-device preview on a phone. `isPreviewSimulator` implies it.
	 *
	 * The broader of the two: use this for behaviour that should differ from a published experience
	 * regardless of which preview surface the user is on, and `isPreviewSimulator` for anything
	 * specific to the synthetic camera and the orbit view.
	 */
	public isPreview = false;

	public isDevRelease: boolean = false;
	public forcedLocale: ILocale | null = null;
	public autoplayVideoCount: number = 10;
	public lmsClient: LmsClient | undefined;
	public sceneTitleById = {} as { [sceneId: string]: string };
	public isLaunched = false;
	public isContentLoaded = false;
	public isReady = false;
	public isWebXREnabledExperience = false;
	public isWebXRImmersiveArSupported = false;
	public isMobileWebXRExperience = false;
	public isHeadsetWebXRExperience = false;
	public isAppClip = false; 
	public isMobileDevice = false;
	public isSafari = navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome') && !navigator.userAgent.includes('Chromium');
	public isNativeHlsSupported = false;
	public session?: XRSession | null;
	public isCookieConsentEnabled: boolean = false;
	public audioContext = new AudioContext();
	public audioStreamDestination = this.audioContext.createMediaStreamDestination();
	public staticGeometryWorkerController = new StaticGeometryWorkerController();
	public bvhWorkerController = new BVHWorkerController();

	constructor() {
		this.lmsClient = getLmsClient();
		if (typeof navigator.permissions !== 'undefined') {
			navigator.permissions.query({ name: 'camera' as PermissionName }).then((permission) => {
				permission.addEventListener('change', () => {
					this._grantedPermissions.camera = permission.state === 'granted';
				});
			});
			navigator.permissions.query({ name: 'gyroscope' as PermissionName }).then((permission) => {
				permission.addEventListener('change', () => {
					this._grantedPermissions.orientation = permission.state === 'granted';
				});
			});
			navigator.permissions.query({ name: 'accelerometer' as PermissionName }).then((permission) => {
				permission.addEventListener('change', () => {
					this._grantedPermissions.motion = permission.state === 'granted';
				});
			});
		}
		const video = document.createElement('video');
		this.isNativeHlsSupported = !!video.canPlayType('application/vnd.apple.mpegurl');
		// console.log('can play hls natively', !!video.canPlayType('application/vnd.apple.mpegurl'))
	}

	init: IJuteInit = async ({ rootEl, launchButton, onLoading, onLoaded, onFirstCameraFrame, onUnsupported, cameraSourcePending, previewSimulator, preview }) => {
		this.isDevRelease = this._version.startsWith('dev/');
		setDebugLogging(this.isDevRelease);

		// Read before anything can reach _launch(): the iframe branch further down launches on its own.
		if (cameraSourcePending && !this._cameraSource) {
			this.isCameraSourcePending = true;
			this._requiredPermissions.camera = false;
		}

		// Nothing in the non-AR scene opens a camera, so requiring the permission would prompt for a
		// device that is never read. `_requestPermissions` settles on its own when nothing is required.
		if (previewSimulator) {
			this.isPreviewSimulator = true;
			this._requiredPermissions.camera = false;
		}

		if (preview || previewSimulator) this.isPreview = true;

		if (this.isDevRelease) {
			console.log('DEV version:', this._version.replace('dev/', ''));
			this._versionSpan = document.createElement('span');
			this._versionSpan.innerText = this._version;
			this._versionSpan.style.position = 'absolute';
			this._versionSpan.style.top = '0px';
			this._versionSpan.style.right = '0px';
			this._versionSpan.style.color = 'red';
			this._versionSpan.style.zIndex = '9999999';
			this._versionSpan.style.fontSize = '24px';
			document.body.appendChild(this._versionSpan);

			this._languageSelection = document.createElement('select');

			this._languageSelection.addEventListener('change', (ev: Event) => {
				const selectedLocale = (ev.target as HTMLSelectElement)?.value as ILocale;
				this.forcedLocale = selectedLocale;
				if (launchButton) launchButton.textContent = getTranslation(ITranslationKey.launch)
			});

			Object.assign(this._languageSelection.style, {
				padding: '6px 10px',
				fontSize: '1.2rem',
				borderRadius: '8px',
				border: '2px solid #007AFF',
				cursor: 'pointer',
				minWidth: '100px',
				position: 'absolute',
				top: '40px',
				right: '10px',
				zIndex: '9999999'
			});

			const locales = Object.keys(translationMap) as ILocale[];

			locales.forEach(locale => {
				const option = document.createElement('option');
				option.value = locale;
				option.textContent = locale;
				this._languageSelection?.appendChild(option);
			})

			document.body.appendChild(this._languageSelection);
		}

		if (this.isQuest || this.isZapbox || this.isPico) {
			this.isHeadset = true;
		}

		this.isMobileDevice = this._isMobileDevice();

		this.isWebXREnabledExperience = (window as any).isWebXREnabledExperience ?? false;
		this._rootEl = rootEl;

		if (this.isIframe) {
			this._rootEl = document.body;
		} else {
			document.body.classList.add('hydrated'); // displays the launch screen UI
		}

		let contentUrl = `${window.location.pathname}content.json`;
		if (!window.location.pathname.endsWith('/')) {
			contentUrl = `${window.location.pathname}/content.json`;
			// After adding the full path to the content.json above, some URLs are still loading incorrectly when the window path doesn't end with a trailing slash
			window.history.replaceState(null, document.title, `${window.location.pathname}/`);
		}

		if (this._environment === 'test') contentUrl = TEST_SOURCE_FILE_NAME;

		const contentDoc = (await (await fetch(contentUrl)).json()) as IContentDoc;
		this.autoplayVideoCount = this._getAutoplayVideoCountFromContentDoc(contentDoc);
		if (typeof (window as any).isWebXRExperience !== 'undefined') {
			this.isWebXREnabledExperience = (window as any).isWebXREnabledExperience;
		} else {
			this.isWebXREnabledExperience = contentDoc.device === 'headset' || !!contentDoc.isWebXREnabledExperience;
		}

		this.isWebXRImmersiveArSupported =  await this._checkWebXRImmersiveARSupport();

		const projectHasWorldTracking = this._checkProjectHasWorldTrackedScenes(contentDoc.componentsById, contentDoc.rootComponentId);
		this.isMobileWebXRExperience =  this.isWebXREnabledExperience && this.isMobileDevice && this.isWebXRImmersiveArSupported && projectHasWorldTracking;

		if (this.isMobileWebXRExperience) setPreferWebXRCamera(true);
		if (this._environment !== 'test') this._initSentry();
		if (typeof contentDoc === 'undefined') throw 'Content not loaded';

		this.isAppClip = isAppClip();

		if (this.isMobileWebXRExperience || this.isAppClip) this._setViewportFitCover();

		// Off `componentsById`, not off the doc itself. Indexing the doc by component id always missed,
		// so this reported `[undefined]` — including in every Sentry report that carried it.
		const rootId = contentDoc?.rootComponentId ?? '';
		this._sceneTypes = ((contentDoc?.componentsById?.[rootId] as IRoot)?.children ?? [])
			.map(id => (contentDoc?.componentsById?.[id] as ISceneComp)?.trackingType)
			.filter(Boolean) as ITrackingTypes[];
		
		// Every flag that gates the camera mount, in one line — the launch path is otherwise silent,
		// so this is what names the failing gate when a spinner never clears.
		debugLog('environment', {
			version: this._version,
			isIframe: this.isIframe,
			isHeadset: this.isHeadset,
			isAppClip: this.isAppClip,
			isMobileDevice: this.isMobileDevice,
			isWebXREnabledExperience: this.isWebXREnabledExperience,
			isWebXRImmersiveArSupported: this.isWebXRImmersiveArSupported,
			isMobileWebXRExperience: this.isMobileWebXRExperience,
			isCameraSourcePending: this.isCameraSourcePending,
			isPreviewSimulator: this.isPreviewSimulator,
			isPreview: this.isPreview,
			device: contentDoc.device,
			sceneTypes: this._sceneTypes,
			requiredPermissions: { ...this._requiredPermissions }
		});

		this._initAnalytics(contentDoc.analyticsTracking);
		this.logEventToAnalytics({
			category: 'General',
			name: 'First load',
			label: 'First load',
			value: 1,
		});

		if (!this._rootEl && !this.isIframe) {
			this._rootEl = document.createElement('div') as HTMLDivElement;
			this._rootEl.style.visibility = 'hidden';
			this._rootEl.style.height = '100%';
			this._rootEl.style.width = '100%';
			this._rootEl.style.position = 'absolute';
			this._rootEl.style.top = '0px';
			this._rootEl.style.left = '0px';
			this._rootEl.style.touchAction = 'none';
			this._rootEl.id = ROOT_ID;
			document.body.append(this._rootEl);
		}

		this._onUnsupported = onUnsupported;
		if (onLoaded) this._onReadyCbs.push(onLoaded);
		if (onFirstCameraFrame) this.onFirstCameraFrame(onFirstCameraFrame);
		filestore.load(contentDoc.mediaFilesById);

		const root = createRoot(this._rootEl!);

		root.render(
			<Sentry.ErrorBoundary onError={this.error}>
				<App contentDoc={contentDoc} />
			</Sentry.ErrorBoundary>
		);

		if (this.isIframe) {
			this.isCookieConsentEnabled = false;
			this.updateCookieConsent(false);
			onLoading?.();
			this._launch();
		} else {
			// Check if there is a 360 scene in the contentDoc
			for (const compId in contentDoc.componentsById) {
				const comp = contentDoc.componentsById[compId];
				if (comp.type === IComponentType.Scene && comp.trackingType === ITrackingTypes.content360) {
					// Only 360 scenes require orientation and motion access
					this._requiredPermissions.orientation = true;
					this._requiredPermissions.motion = true;
					break;
				}
			}
			launchButton?.addEventListener?.('click', () => {
				this._launchProject(onLoading);
			});
		}

		document.body.classList.add('hydrated');
		document.dispatchEvent(new CustomEvent('jute:ready', { detail: this }));
	};

	private _launchProject = async (loadingCb?: (() => any) | undefined) => {
		debugLog('launch button handler ran');
		loadingCb?.();
		this._launch();
		if (!isSafariWithAudioContextBug()) this.audioContext.resume();

		if (!this.isWebXREnabledExperience || !this.isHeadset) return;

		const optionalFeatures = ['local-floor', 'bounded-floor'];
		const requiredFeatures = [];
		if (!this.isPico) requiredFeatures.push('hit-test');
		this.session = await intialiseWebxrSession({ requiredFeatures, optionalFeatures });
		if (this.session) this._onSessionStarted(this.session);
	};

	public updateCookieConsent = (consent: boolean): void => {
		// loop over the IAnalyticsProvider enum and updateCookieConsent on all
		cookiesPermitted(consent);
		this._analyticsProviderDict[IAnalyticsProvider.googleAnalytics]?.updateCookieConsent(consent);
		this._analyticsProviderDict[IAnalyticsProvider.microsoftClarity]?.updateCookieConsent(consent);
	};

	private _initAnalytics = (analyticsTracking?: IAnalyticsTrackingDict) => {
		const gTagIds = analyticsTracking?.[IAnalyticsProvider.googleAnalytics];
		const msClarityTagIds = analyticsTracking?.[IAnalyticsProvider.microsoftClarity];
		this.isCookieConsentEnabled = !!gTagIds || !!msClarityTagIds;

		if (gTagIds) this._analyticsProviderDict[IAnalyticsProvider.googleAnalytics] = new GoogleAnalyticsProvider(gTagIds);
		if (msClarityTagIds) this._analyticsProviderDict[IAnalyticsProvider.microsoftClarity] = new MsClarityAnalyticsProvider(msClarityTagIds);
	};

	public logEventToAnalytics = (event: IAnalyticsEvent): void => {
		// console.log('Log event to analytics, ', event);
		// loop over the IAnalyticsProvider enum and sendEvent on all
		this._analyticsProviderDict[IAnalyticsProvider.googleAnalytics]?.sendEvent(event);
		this._analyticsProviderDict[IAnalyticsProvider.microsoftClarity]?.sendEvent(event);
	};

	private _initSentry = (): void => {
		Sentry.init({
			dsn: 'https://1636ccf870c64cbbac6a9491a55c0321@o160191.ingest.sentry.io/5689090',
			environment: this._environment,
			release: this._version,
			integrations: [Sentry.httpClientIntegration()],
			tracesSampleRate: 0.9,
			ignoreErrors: ['AbortError: The operation was aborted.', 'TypeError: cancelled'],
			tracesSampler: ({ name }) => {
				// Reduce sample rate to 25%
				if (name.includes('device-orientation')) return 0.25;
				// Otherwise, inherit the sample sampling decision of the incoming trace, or use a fallback sampling rate.
				return 0.9;
			},
		});
	};

	/**
	 * Record a permission request that was refused or errored.
	 *
	 * Deliberately leaves the matching `_grantedPermissions` flag FALSE, so `_arePermissionsGranted()`
	 * keeps reporting the truth — the point of catching is only to let `_requestPermissions()` settle.
	 */
	private _reportPermissionFailure = (permission: 'camera' | 'orientation' | 'motion', err: unknown): void => {
		console.warn(`jute manager: ${permission} permission was not granted`, err);
		if (this._environment === 'test') return;
		Sentry.withScope((scope) => {
			scope.setExtra('permission', permission);
			scope.setExtra('error', err instanceof Error ? `${err.name}: ${err.message}` : String(err));
			scope.setExtra('required permissions', { ...this._requiredPermissions });
			scope.setExtra('granted permissions', { ...this._grantedPermissions });
			Sentry.captureMessage(`Jute permission not granted: ${permission}`);
		});
	};

	/**
	 * Resolves once every REQUIRED permission has been asked for — granted or not.
	 *
	 * Each request needs a `.catch()` for one reason: this promise gates `_launch()`, and every branch
	 * below must set its `hasChecked…` flag or `permissionChecked()` never calls `res()`. Without them a
	 * refusal left the promise permanently pending, so `_launch()` never ran: no launch callbacks, no
	 * `loaded()`, and the launch screen's spinner span forever with nothing logged anywhere.
	 *
	 * iOS Safari hits this reliably. `getUserMedia` and the iOS 13+ `requestPermission()` APIs both
	 * refuse outside a real user gesture, and a synthetic `.click()` on the launch button is not one —
	 * which is exactly how an automated re-launch (e.g. a live-preview harness reloading the page)
	 * reaches them.
	 */
	private _requestPermissions = () => {
		return new Promise<void>((res) => {
			let hasCheckedCamera = !this._requiredPermissions.camera;
			let hasCheckedOrientation = !this._requiredPermissions.orientation;
			let hasCheckedMotion = !this._requiredPermissions.motion;

			const permissionChecked = () => {
				if (hasCheckedCamera && hasCheckedOrientation && hasCheckedMotion) res();
			};

			if (this._requiredPermissions.camera && !this._grantedPermissions.camera) {
				// Request camera permission
				navigator.mediaDevices.getUserMedia({ video: true }).then((stream) => {
					stream.getTracks().forEach((track) => track.stop()); // Stop the stream immediately
					this._grantedPermissions.camera = true;
					hasCheckedCamera = true;
					permissionChecked();
				}).catch((err) => {
					this._reportPermissionFailure('camera', err);
					hasCheckedCamera = true;
					permissionChecked();
				});
			}

			if (this._requiredPermissions.orientation && !this._grantedPermissions.orientation) {
				// Request device orientation permission (iOS 13+)
				if (typeof (DeviceOrientationEvent as any).requestPermission === 'function') {
					(DeviceOrientationEvent as any).requestPermission().then((orientationPermission: any) => {
						this._grantedPermissions.orientation = orientationPermission === 'granted';
						hasCheckedOrientation = true;
						permissionChecked();
					}).catch((err: unknown) => {
						this._reportPermissionFailure('orientation', err);
						hasCheckedOrientation = true;
						permissionChecked();
					});
				} else {
					// no permissions required
					hasCheckedOrientation = true;
					permissionChecked();
				}
			}

			if (this._requiredPermissions.motion && !this._grantedPermissions.motion) {
				// Request device motion permission (iOS 13+)
				if (typeof (DeviceMotionEvent as any).requestPermission === 'function') {
					(DeviceMotionEvent as any).requestPermission().then((motionPermission: any) => {
						this._grantedPermissions.motion = motionPermission === 'granted';
						hasCheckedMotion = true;
						permissionChecked();
					}).catch((err: unknown) => {
						this._reportPermissionFailure('motion', err);
						hasCheckedMotion = true;
						permissionChecked();
					});
				} else {
					// no permissions required
					hasCheckedMotion = true;
					permissionChecked();
				}
			}

			// When NOTHING is required every branch above is skipped, so without this the promise never
			// settles and _launch() never runs — the launch screen just spins. Reachable on headsets and
			// whenever a host camera source removes the camera requirement.
			permissionChecked();
		});
	};

	private _arePermissionsGranted = () => {
		return (
			this._grantedPermissions.camera === this._requiredPermissions.camera &&
			this._grantedPermissions.orientation === this._requiredPermissions.orientation &&
			this._grantedPermissions.motion === this._requiredPermissions.motion
		);
	};

	private _launch = (): void => {
		debugLog('_launch: requesting permissions', { ...this._requiredPermissions });
		this._requestPermissions().then(() => {
			debugLog('_launch: permissions settled', {
				granted: { ...this._grantedPermissions },
				launchCallbacks: this._launchCbs.length
			});
			// console.log('Required permissions:', JSON.stringify(this._requiredPermissions))
			// console.log('Granted permissions:', JSON.stringify(this._grantedPermissions))
			for (let i = 0; i < this._launchCbs.length; i++) {
				this._launchCbs[i]?.();
			}
			if (this.isDevRelease && this._versionSpan) {
				if (this._versionSpan) document.body.removeChild(this._versionSpan)
				if (this._languageSelection) document.body.removeChild(this._languageSelection)
			};

			this.isLaunched = true;
			this.lmsClient?.initialize();
			this._loadingStartDate = new Date();
			
			setTimeout(() => {
				if (!this._firstFrameVisible) {
					// On screen this is an unexplained spinner that never clears, and until now the only
					// report went to Sentry — so the person watching it had nothing to go on.
					console.warn('jute manager: no camera frame 5s after launch — the experience cannot start', debugFormat({
						firstFrameVisible: this._firstFrameVisible,
						contentLoaded: this.isContentLoaded,
						requiredPermissions: { ...this._requiredPermissions },
						grantedPermissions: { ...this._grantedPermissions },
						permissionsGranted: this._arePermissionsGranted(),
						isAppClip: this.isAppClip,
						isMobileWebXRExperience: this.isMobileWebXRExperience,
						isCameraSourcePending: this.isCameraSourcePending,
						isHeadset: this.isHeadset,
						isIframe: this.isIframe,
						sceneTypes: this._sceneTypes
					}));
					Sentry.withScope((scope) => {
						scope.setExtra('jute state', { 
							firstFrameVisible: this._firstFrameVisible, 
							contentLoaded: this.isContentLoaded, 
							isAppClip: Jute.isAppClip, 
							isMobileWebXRExperience: Jute.isMobileWebXRExperience,
							isHeadset: Jute.isHeadset,
							isIframe: Jute.isIframe,
							sceneTypes: this._sceneTypes
						});
						Sentry.captureMessage('First camera frame not available 5s after project launch');
					})};
			}, 5000)
		});

		window.addEventListener('beforeunload', () => {
			if (this.isReady) return;
			if (!this._arePermissionsGranted()) return; // Not all permissions are granted, so we don't log the error
			const timeInSecSinceLoadingStart = (new Date().getTime() - this._loadingStartDate!.getTime()) / 1000;
			Sentry.withScope((scope) => {
				scope.setExtra('loading data', { timeInSecSinceLoadingStart, firstFrameVisible: this._firstFrameVisible, contentLoaded: this.isContentLoaded, permissionsGranted: this._arePermissionsGranted() });
				Sentry.captureMessage('Jute was closed before content loaded');
			});
		});
	};

	onLaunch = (cb: () => any): void => {
		this._launchCbs.push(cb);
	};

	private _onSessionStarted = (session: XRSession): void => {
		for (let i = 0; i < this._onSessionStartedCbs.length; i++) {
			this._onSessionStartedCbs[i]?.(session);
		}
	};

	onSessionStarted = (cb: (session: XRSession) => any): void => {
		this._onSessionStartedCbs.push(cb);
	};

	private _ready = (): void => {
		console.log('jute manager: ready to show experience');
		(this._rootEl as HTMLElement).style.visibility = 'visible';
		const meta = document.createElement('meta');
		meta.name = 'viewport';
		meta.content = 'width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=1, user-scalable=no';
		document.getElementsByTagName('head')[0].appendChild(meta);

		this.isReady = true;

		for (let i = 0; i < this._onReadyCbs.length; i++) {
			this._onReadyCbs[i]?.();
		}
		if (!this._loadingStartDate) this._loadingStartDate = new Date();

		const timeInSecSinceLoadingStart = (new Date().getTime() - this._loadingStartDate!.getTime()) / 1000;

		if (timeInSecSinceLoadingStart > 5 && !this._firstFrameVisible) {
			Sentry.withScope((scope) => {
				scope.setExtra('loading data', { timeInSecSinceLoadingStart });
				Sentry.captureMessage('Camera not visible 5s to load content');
			});
		}

		if (timeInSecSinceLoadingStart > 20) {
			Sentry.withScope((scope) => {
				scope.setExtra('loading data', { timeInSecSinceLoadingStart });
				Sentry.captureMessage('Jute required longer than 20s to load content');
			});
		}
	};

	onReady = (cb: () => any): void => {
		this._onReadyCbs.push(cb);
	};

	private _contentLoaded = (): void => {
		this.isContentLoaded = true;
		// console.log('jute manager: content loaded');
		for (let i = 0; i < this._contentLoadedCbs.length; i++) {
			this._contentLoadedCbs[i]();
		}
	};

	onContentLoaded = (cb: () => any): void => {
		this._contentLoadedCbs.push(cb);
	};

	unsupported = (): void => {
		this._onUnsupported?.();
	};

	loading = (): void => {
		this._onLoading?.();
	};

	loaded = (): void => {
		this._contentLoaded();
		debugLog('content loaded', this._firstFrameVisible ? '— first frame already in, going ready' : '— WAITING on the first camera frame');
		if (this._firstFrameVisible) {
			this._ready?.();
		} else {
			this._onloadedBeforeFirstFrame = true;
		}
	};

	testTimeChange = (t: number): void => {
		this._onTestTimeChange?.(t);
	};

	onTestTimeChange = (cb: (t: number) => any): void => {
		this._onTestTimeChange = cb;
	};

	/**
	 * Capability marker for `setPreviewRoomDark`, read the same way `liveUpdateVersion` is: a host that
	 * finds it absent leaves the room alone rather than calling a method this build does not have.
	 */
	public previewRoomVersion = 1;

	/**
	 * Draw the DESKTOP PREVIEW's room dark, or put it back.
	 *
	 * Only a world-tracked scene has a room of JUTE's own — `PreviewRoom`'s floor and the sky behind
	 * it. Every other preview scene shows Designer's simulated room through the camera feed, which
	 * Designer darkens itself; this exists because that one case is drawn in here, out of its reach.
	 *
	 * A no-op in a published experience: nothing outside the preview ever calls it, and nothing inside
	 * mounts `PreviewRoom` to see it.
	 */
	public setPreviewRoomDark = (dark: boolean): void => {
		useUserStore.getState().dispatch?.({ type: IUserActions.SET_PREVIEW_ROOM_DARK, payload: !!dark });
	};

	/**
	 * Capability marker for `setExperienceMuted`, read the way `previewRoomVersion` is.
	 */
	public experienceMutedVersion = 1;

	/**
	 * Silence the whole experience, or let it play again.
	 *
	 * The same two reducers the in-experience sound button uses, so background audio, every scene's
	 * audio and every video are all covered — muting them piecemeal from outside would miss whatever
	 * starts after the call.
	 */
	public setExperienceMuted = (muted: boolean): void => {
		useActionStateStore.getState().dispatch?.({
			type: muted ? IEntityActionTypes.MUTE_EXPERIENCE : IEntityActionTypes.UNMUTE_EXPERIENCE
		});
	};

	/** Whether `setExperienceMuted` last silenced the experience. */
	get isExperienceMuted(): boolean {
		return !!useActionStateStore.getState().experienceIsMuted;
	}

	/**
	 * Capability marker for `applyContentUpdate`. Absent on any build that predates it, which is how a
	 * host (e.g. the live-preview bridge) decides whether it can update in place or must fall back to
	 * reloading the page. Bump only on a breaking change to the method's contract.
	 */
	public liveUpdateVersion = 1;

	/**
	 * Apply a new content doc to the RUNNING experience, without rebooting.
	 *
	 * The point is the camera: a reload re-runs `getUserMedia`, which on iOS Safari means a fresh
	 * permission request the user must tap (and which is refused outright without a real gesture). Nothing
	 * in the camera path reads the content doc, so pushing new content through the stores leaves the
	 * camera and the AR pipeline entirely alone.
	 *
	 * Returns false — having changed nothing — if the doc is invalid, rather than half-applying it.
	 *
	 * NOT suitable for every change. Tracking-image config is consumed once at init, and the SET of
	 * tracking types across scenes drives whether the camera wrapper is mounted at all, so changing either
	 * needs a reload. The caller is expected to classify; this method assumes it has.
	 *
	 * THE ORDER BELOW IS LOAD-BEARING. Additions must be registered before the dispatch so new assets can
	 * resolve; disposal and eviction must happen strictly after it, once React has unmounted whatever was
	 * rendering the removed ones. Freeing early shows up as a blank or black mesh with no error.
	 */
	public applyContentUpdate = async (doc: IContentDoc): Promise<boolean> => {
		const report = isValidContentDoc(doc);
		if (!report.isValid) {
			console.error('[jute-live] refusing an invalid content doc — nothing applied', report.report);
			return false;
		}

		// The scene list is the one thing an update must not change, so check it here rather than trusting
		// the caller to have classified correctly. `applyContentToStores` leaves `activeSceneId` alone on
		// an update by design, so a doc with a different scene list leaves it naming a scene
		// `componentsById` no longer holds — which strands the tracker gates in `TrackerGroups` and throws
		// in whatever indexes that id next. Refusing sends the caller down its reload path instead.
		//
		// Against the STORE, which is what the mounted React tree reads, not `content-apply`'s own copy.
		const { componentsById: heldComponentsById, rootId: heldRootId } = useDataStore.getState();
		const heldSceneSig = sceneSignatureFor(heldComponentsById, heldRootId);
		const incomingSceneSig = sceneSignature(doc);
		if (heldSceneSig !== incomingSceneSig) {
			console.warn(
				'[jute-live] refusing a doc whose scene list or tracking types changed — this needs a reboot, not a live update ' +
					`(holding "${heldSceneSig || '<nothing>'}", offered "${incomingSceneSig || '<nothing>'}")`
			);
			return false;
		}

		try {
			// 1. Register new media ids (and collect the orphans) BEFORE anything renders against them.
			//    merge() rather than load(): load() would rebuild every FsFile and throw away every
			//    decoded blob, texture and parsed model in the project on every edit.
			const { added, removed } = filestore.merge(doc.mediaFilesById);

			// 2. Push the content in, reusing object identity for unchanged entities.
			const { componentsById, rootId } = applyContentToStores(doc, { initial: false });

			// 3. Refresh the boot-time values derived from the doc that are cheap and safe to recompute.
			this.autoplayVideoCount = this._getAutoplayVideoCountFromContentDoc(doc);

			// 4. Fetch anything newly referenced. preloadGltf already skips URLs it has, so this is a
			//    no-op for unchanged assets.
			if (added.length) {
				void preloadAssets({ componentsById, rootId }).catch((err) =>
					console.warn('[jute-live] asset preload after update failed', err)
				);
			}

			// 5. Free the orphans — after the dispatch, so nothing on screen is disposed.
			queueMicrotask(() => removed.forEach((file) => file.dispose()));

			// 6. Reclaim the URL-keyed caches, once transitions have settled. Deferred past the longest
			//    scene transition: a scene mid-exit is still rendering assets the new doc no longer lists.
			setTimeout(() => this._evictUnreferencedAssets(componentsById, rootId), EVICT_GRACE_MS);

			// console.log(`[jute-live] update applied (${added.length} new asset(s), ${removed.length} orphaned)`);
			return true;
		} catch (err) {
			console.error('[jute-live] applying a content update failed', err);
			return false;
		}
	};

	/**
	 * Drop cached models and textures the current doc no longer references.
	 *
	 * Three separate owners retain a model, and missing any one of them reclaims nothing:
	 *   - `modelCache` (parsed GLTF + raw GLB) in the shared submodule,
	 *   - `globalCache` (textures), likewise,
	 *   - the BVH worker controller, which stashes each GLB for worker restarts AND has its own
	 *     structure-cloned copy inside the worker.
	 *
	 * The keep-set comes from `collectAssetUrls`, the same walk the preloader uses — if the two disagreed,
	 * this would dispose something still on screen.
	 */
	private _evictUnreferencedAssets = (componentsById: { [id: string]: IComponentUnion }, rootId: string): void => {
		// ALWAYS logs, including when it evicts nothing. An earlier version logged only on a non-zero
		// result, which made "no output" mean any of: the timer never fired, the walk threw, the keep-set
		// was wrong, or the asset was never cached in the first place. Reclamation you cannot observe is
		// reclamation you cannot trust, so the counts and the inputs that produced them are always shown.
		try {
			const { imgUrls, model3dUrls } = collectAssetUrls({ componentsById, rootId });
			const models = new Set(model3dUrls);
			const evictedModels = evictModelsNotIn(models);
			const releasedBuffers = this.staticGeometryWorkerController?.releaseModelsNotIn(models) ?? [];
			// Only project media is a candidate. Everything under a filestore path is; bundled UI assets
			// (the broken-image icon and friends) are not, and disposing those would break them for good.
			const evictedTextures = evictTexturesNotIn(new Set(imgUrls), (url) => url.includes('/fs/'));
			// console.log(
				// `[jute-live] evicted ${evictedModels.length} model(s), ${evictedTextures.length} texture(s), ` +
					// `released ${releasedBuffers.length} worker buffer(s)`,
				// {
					// evictedModels,
					// evictedTextures,
					// releasedBuffers,
					// The keep-set, so a wrong one is visible rather than inferred.
					// keptModelUrls: model3dUrls,
					// keptImageUrls: imgUrls,
					// hasWorkerController: !!this.staticGeometryWorkerController
				// }
			// );
		} catch (err) {
			// Never let a reclamation problem break a preview that is otherwise working.
			console.warn('[jute-live] asset eviction failed', err);
		}
	};

	testClickEntity = (id: string): void => {
		for (let i = 0; i < this._onTestEntityClickCbs.length; i++) {
			this._onTestEntityClickCbs[i](id);
		}
	};

	onTestClickEntity = (cb: (id: string) => any): void => {
		this._onTestEntityClickCbs.push(cb);
	};

	firstCameraFrame = (): void => {
		console.log('jute manager: first camera frame');
		debugLog('first camera frame', this._onloadedBeforeFirstFrame ? '— content already loaded, going ready' : '— WAITING on content to load');
		this._firstFrameVisible = true;
		this._onFirstCameraFrameCbs.forEach((fn) => fn());
		if (this._onloadedBeforeFirstFrame || this._environment === 'test') {
			this._ready?.();
			// (this._rootEl as HTMLElement).style.visibility = 'visible';
		}
	};

	error = (): void => {
		showDefaultErrorPage();
	};

	public onFirstCameraFrame = (cb: IOnFirstCameraFrameCb) => {
		this._onFirstCameraFrameCbs.push(cb);
	};

	// This method is called from React
	showWebembedHintGraphic = (show: boolean, _document: Document): void => {
		const existingContainer = _document.getElementById('hintGraphicContainer');
		if (!show) {
			if (existingContainer) existingContainer.style.visibility = 'hidden';
			return;
		}
		if (show && existingContainer) {
			existingContainer.style.visibility = 'visible';
			return;
		}
		const newContainer = _document.createElement('div');
		newContainer.id = 'hintGraphicContainer';
		newContainer.style.zIndex = '999999';
		newContainer.style.position = 'absolute';
		newContainer.style.left = '0';
		newContainer.style.right = '0';
		newContainer.style.height = '100%';
		newContainer.style.width = '100%';
		newContainer.style.display = 'flex';
		newContainer.style.justifyContent = 'center';
		newContainer.style.alignItems = 'center';
		newContainer.style.pointerEvents = 'none';
		const img = _document.createElement('img');
		img.src = HintGraphicSvgUrl;
		img.style.zIndex = '99';
		img.style.width = '60px';
		img.style.height = '50px';
		newContainer.appendChild(img);
		document.body.appendChild(newContainer);
	};

	private _isMobileDevice = (): boolean => {
		// Only works for non-iOS devices
		if ('userAgentData' in navigator) {
			const uaData = (navigator as any).userAgentData;
			if (typeof uaData.mobile === 'boolean') {
				return uaData.mobile;
			}
		}

		// ios devices where browsers do not support userAgentData
		const ua = navigator.userAgent || navigator.vendor || (window as any).opera;
		const isMobileUA = /android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(ua.toLowerCase());

		// for tablets in desktop mode
		const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

		// safeguard against false positive results for android based headset browsers
		const isHeadsetUA = /oculus|quest|vive|rift|hololens|zapbox|windows mixed reality/i.test(ua.toLowerCase());

		return (isMobileUA || isTouchDevice) && !isHeadsetUA;
	};

	private _checkWebXRImmersiveARSupport = async () => {
		if (!('xr' in navigator)) {
			console.log('WebXR API not found.');
			return false;
		}
		try {
			const isImmersiveArSupported = !!(await navigator?.xr?.isSessionSupported('immersive-ar'));
			// if (isArSupported) console.log('Immersive AR (Augmented Reality) is supported!');
			// else console.log('Immersive AR not supported on this device/hardware.');
			return isImmersiveArSupported;
		} catch (error) {
			// console.error('Error checking session support:', error);
			return false;
		}
	};

	private _checkProjectHasWorldTrackedScenes = (componentsById: IComponentsById, rootId: string): boolean => {
		const root = componentsById?.[rootId || ''] as IRoot | undefined;
		const numWorldTrackedScenes = root?.children?.filter(id => (componentsById?.[id] as ISceneComp).trackingType === ITrackingTypes.world)?.length;
		return (numWorldTrackedScenes ?? 0) > 0;
	}

	private _setViewportFitCover = (): void => {
		// Try to find existing viewport meta tag
		let viewport: HTMLMetaElement | null = document.querySelector('meta[name="viewport"]');
	  
		// Create one if not found
		if (!viewport) {
		  viewport = document.createElement('meta');
		  viewport.name = 'viewport';
		  document.head.appendChild(viewport);
		}
	  
		// Get existing content safely
		let content: string = viewport.getAttribute('content') || '';
	  
		// Remove any existing viewport-fit declarations
		content = content
		  .split(',')
		  .map(s => s.trim())
		  .filter(s => !s.startsWith('viewport-fit='))
		  .join(', ');
	  
		// Add viewport-fit=cover at the end
		if (content) content += ', ';
		content += 'viewport-fit=cover';
	  
		// Apply the new content string
		viewport.setAttribute('content', content);
	};

	private _getAutoplayVideoCountFromContentDoc = (contentDoc?: IContentDoc): number => {
		if (contentDoc === undefined) return 10;
		let count = 0;
		for (const compId in contentDoc.componentsById) {
			const comp = contentDoc.componentsById[compId];
			if (comp.type === IComponentType.Video && comp.autoplay) {
				count++;
			}
		}
		return count;
	}

	get isIframe(): boolean {
		const params = new URL(window.location.href).searchParams;
		const isEmbeded = params.get('mode') === IMode.nonar;
		const isIframe = () => {
			try {
				return window.self !== window.top;
			} catch (e) {
				return true;
			}
		};

		return isEmbeded || isIframe();
	}

	/**
	 * Is the WHOLE EXPERIENCE rendering the non-AR scene — every scene's content at the origin under an
	 * orbit camera — rather than tracked content over a camera feed?
	 *
	 * Only a web embed is. Kept separate from `isIframe`, which also means "no launch screen, no audio,
	 * embed UI", so that anything really asking about the *scene* reads as asking about the scene.
	 *
	 * The desktop preview is deliberately NOT included, even though its world-tracked scenes orbit.
	 * `mode` is a tree-shape decision taken once per boot — flipping it relocates every scene and
	 * remounts the lot — so the preview stays in AR for the whole session and orbits per scene by
	 * handing the render camera over instead. See PREVIEW_OVERVIEW.md §10.
	 */
	get usesOrbitCamera(): boolean {
		return this.isIframe;
	}

	get isZapbox(): boolean {
		return navigator.userAgent.toLowerCase().includes('zapbox');
	}

	get isPico(): boolean {
		return navigator.userAgent.includes('PICO') || navigator.userAgent.includes('Pico') || navigator.userAgent.includes('PicoBrowser');
	}

	get isQuest(): boolean {
		return navigator.userAgent.includes('Quest');
	}

	get version(): string {
		return this._version || '';
	}

	set version(value: string) {
		this._version = value;
	}

	get environment(): IEnvironment {
		return this._environment;
	}

	set environment(env: IEnvironment) {
		this._environment = env;
	}

	get testTransitionTimeStepsMs(): number {
		return this._testTransitionTimeStepsMs || 10;
	}

	set testTransitionTimeStepsMs(value: number) {
		this._testTransitionTimeStepsMs = value;
	}

	get cameraSource(): ICameraSource | undefined {
		return this._cameraSource;
	}

	/**
	 * Use `el` as the frame source instead of the device camera.
	 *
	 * Prefer a <canvas>. The library's element source resets its "has frames" flag on every start()
	 * and pause()/resume() for a <video> and then waits for a `loadedmetadata` that an already-loaded
	 * element never fires again, so a video source dies silently on the first tab switch. That reset is
	 * guarded by an `instanceof HTMLVideoElement` check a canvas skips. A canvas also has no
	 * `requestVideoFrameCallback`, so a still image keeps feeding the tracker instead of stalling it.
	 *
	 * Callable any time before launch — `window.Jute` exists as soon as the script evaluates, and the
	 * camera only mounts once the user taps Launch.
	 */
	setCameraSource = (el: ICameraSource): void => {
		this._cameraSource = el;
		this.isCameraSourcePending = false;
		this._requiredPermissions.camera = false;
		this._cameraSourceCbs.forEach((cb) => cb());
	};

	/** Subscribe to `setCameraSource`. Returns an unsubscribe fn. */
	onCameraSourceChange = (cb: () => unknown): (() => void) => {
		this._cameraSourceCbs.push(cb);
		return () => {
			this._cameraSourceCbs = this._cameraSourceCbs.filter((c) => c !== cb);
		};
	};

	/**
	 * Report the scene the experience is now showing.
	 *
	 * Called by the scene tree, not by the host. Its own runtime scene is the only truthful answer here:
	 * an experience navigates itself via triggers and target recognition, so the editing scene in a host
	 * app can say something completely different.
	 */
	activeSceneChanged = (sceneId: string, trackingType: ITrackingTypes): void => {
		this._activeScene = { sceneId, trackingType };
		this._activeSceneCbs.forEach((cb) => cb(sceneId, trackingType));
	};

	/**
	 * Subscribe to scene changes. Returns an unsubscribe fn.
	 *
	 * The current scene is delivered immediately if there already is one. A host subscribes from another
	 * document once JUTE has loaded, which is always AFTER the first scene has been decided — without
	 * the replay it would hear nothing until the user navigated, and so would set up for the wrong scene.
	 */
	onActiveSceneChange = (cb: (sceneId: string, trackingType: ITrackingTypes) => unknown): (() => void) => {
		this._activeSceneCbs.push(cb);
		if (this._activeScene) cb(this._activeScene.sceneId, this._activeScene.trackingType);
		return () => {
			this._activeSceneCbs = this._activeSceneCbs.filter((c) => c !== cb);
		};
	};

	/**
	 * Capability marker for `onScreenContentOverflow`, read the way `previewRoomVersion` is: a host that
	 * finds it absent shows no warning rather than calling a method this build does not have.
	 */
	public screenContentOverflowVersion = 1;

	/**
	 * Report which screen-relative entities the CURRENT viewport cuts off.
	 *
	 * Called by the scene tree, not by the host, and only in the preview simulator. Measured from each
	 * entity's AUTHORED transform rather than where it happens to be: preset animations deliberately fly
	 * screen content off screen, and a looped one never comes to rest, so a report driven by live
	 * positions would be wrong most of the time. Scene transitions need no such care — they skip screen
	 * content entirely (see EntityLevelFadeGroup).
	 */
	screenContentOverflowChanged = (report: IScreenContentOverflow): void => {
		this._screenOverflow = report;
		debugLog(
			'screen content overflow:',
			report.entityIds.length ? `${report.entityIds.length} clipped (${report.anchors.join(',')}) off ${report.edges.join(',')}` : 'all clear'
		);
		this._screenOverflowCbs.forEach((cb) => cb(report));
	};

	/**
	 * Subscribe to overflow reports. Returns an unsubscribe fn.
	 *
	 * The current report is replayed immediately if there is one, for the same reason
	 * `onActiveSceneChange` does it: a host in another document subscribes once JUTE has loaded, which
	 * can easily be after the first report, and without the replay it would show nothing until the
	 * viewport next changed.
	 */
	onScreenContentOverflow = (cb: (report: IScreenContentOverflow) => unknown): (() => void) => {
		this._screenOverflowCbs.push(cb);
		if (this._screenOverflow) cb(this._screenOverflow);
		return () => {
			this._screenOverflowCbs = this._screenOverflowCbs.filter((c) => c !== cb);
		};
	};

	/** The last overflow report, for a host that would rather poll than subscribe. */
	get screenContentOverflow(): IScreenContentOverflow | undefined {
		return this._screenOverflow;
	}

	/**
	 * Let the embedding page drive the preview simulator's orbit camera.
	 *
	 * Pointer drag, pinch and wheel already reach OrbitControls on their own; this exists for the host's
	 * own on-screen controls, which sit in a different document and cannot touch the camera directly.
	 * Registered by OrbitControlsWrapper, so it is undefined until the scene has mounted.
	 */
	registerPreviewCameraControls = (handle: IPreviewCameraControls | undefined): void => {
		this._previewCameraControls = handle;
	};

	/** Orbit by radians. Positive azimuth swings the camera left, positive polar raises it. */
	previewOrbit = (deltaAzimuth: number, deltaPolar: number): void => this._previewCameraControls?.orbit(deltaAzimuth, deltaPolar);

	/**
	 * Slide the camera and its orbit target together, in fractions of the visible height at that
	 * target: positive right and up. Unlike an orbit, this moves the pivot, so `previewResetView` is
	 * the only way back from a pan that has left the content behind.
	 */
	previewPan = (deltaRight: number, deltaUp: number): void => this._previewCameraControls?.pan(deltaRight, deltaUp);

	/**
	 * Walk the camera and its orbit target forward, in fractions of the visible height at that target.
	 *
	 * Along the GROUND in the direction the camera faces, not along the view axis — a camera pitched
	 * down should walk across a world scene rather than burrow into its floor. That is the whole
	 * difference between this and `previewZoom`, which closes on the pivot without ever passing it.
	 */
	previewWalk = (deltaForward: number): void => this._previewCameraControls?.walk(deltaForward);

	/** `factor` multiplies the distance to the orbit target: below 1 moves closer, above 1 further away. */
	previewZoom = (factor: number): void => this._previewCameraControls?.zoom(factor);

	/** Back to the framing the scene was first fitted to. */
	previewResetView = (): void => this._previewCameraControls?.reset();

	/**
	 * Let the embedding page turn a 360 scene's camera.
	 *
	 * Registered by `DragControlsWrapper` while a 360 scene is the enabled one, so it is undefined on
	 * every other scene — which is what stops a host that has not noticed the scene change from turning
	 * a camera nobody is looking through.
	 */
	registerPreviewLookControls = (handle: IPreviewLookControls | undefined): void => {
		this._previewLookControls = handle;
	};

	/**
	 * Turn by radians, using the same signs as `previewOrbit` so a host needs one key map for both:
	 * positive azimuth looks left, positive polar looks up.
	 */
	previewLook = (deltaAzimuth: number, deltaPolar: number): void => this._previewLookControls?.look(deltaAzimuth, deltaPolar);
}
